import { AsyncPipe, DatePipe, LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TippyDirective } from '@ngneat/helipopper';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, merge, mergeMap, of, switchMap } from 'rxjs';

import { ToastComponent } from '@locumsnest/components/src/lib/atoms/toast/toast.component';
import { LocumsNestComponent } from '@locumsnest/components/src/lib/core/locums-nest-component';
import { onboardingNavItems } from '@locumsnest/components/src/lib/interfaces/nav-item';
import { SideNavBarComponent } from '@locumsnest/components/src/lib/organisms/side-nav-bar/side-nav-bar.component';
import type { IMicroAppListItemState } from '@locumsnest/core/src/lib/micro-app/interfaces';
import { MicroAppService } from '@locumsnest/core/src/lib/micro-app/micro-app.service';
import {
  CheckForNotificationsPermissionSignal,
  IPushMessage,
  PushMessagesService,
} from '@locumsnest/firebase/src';
import { GetCommentsForRequestSignal } from '@locumsnest/zendesk/comments/src/lib/+state/zendesk-comments.signals';
import {
  FetchRequestByIdSignal,
  ZendeskRequestsModule,
  ZendeskRequestsService,
} from '@locumsnest/zendesk/requests/src';

import { AuthGroupService } from '../../../auth-group/+state/auth-group.service';
import { ExternalStaffingProviderOfficerService } from '../../../external-staffing-provider-officer/+state/external-staffing-provider-officer.service';
import { ExternalStaffingProviderService } from '../../../external-staffing-provider/+state/external-staffing-provider.service';
import { HealthCheckService } from '../../../health-check/+state/health-check.service';
import { HealthCheckModule } from '../../../health-check/health-check.module';
import { HospitalOfficerService } from '../../../hospital-officer/+state/hospital-officer.service';
import { HospitalService } from '../../../hospital/+state/hospital.services';
import { PassportFilterService } from '../../../passport/+state/passport-filter.service';
import { FilterService } from '../../+state/filter.service';
import {
  InitializeLayoutContainerSignal,
  ShowFiltersPanelSignal,
  ShowHideHeaderFilterInformationSignal,
  ShowMessagingPanelSignal,
  ShowProductPanelSignal,
  ToggleFiltersPanelSignal,
  ToggleProductPanelSignal,
} from '../../+state/ui/ui.signals';
import { getServerName, PRODUCT_CODES } from '../../constants';
import { CoreModule } from '../../core.module';
import { LayoutService, RouterService } from '../../services';
import { ChameleonService } from '../../services/chameleon/chameleon.service';
import {
  filterMicroAppListForOfficerAuthGroups,
  selectVisibleNavItems,
} from './layout-container-nav-gating';

@Component({
  selector: 'locumsnest-layout-container',
  templateUrl: './layout-container.component.html',
  styleUrls: ['./layout-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    SideNavBarComponent,
    ToastComponent,
    CoreModule,
    HealthCheckModule,
    ZendeskRequestsModule,
    TippyDirective,
    AsyncPipe,
    DatePipe,
    LowerCasePipe,
  ],
})
export class LayoutContainerComponent extends LocumsNestComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private hospitalService = inject(HospitalService);
  private hospitalOfficerService = inject(HospitalOfficerService);
  private externalStaffingProviderOfficerService = inject(ExternalStaffingProviderOfficerService);
  private externalStaffingProviderService = inject(ExternalStaffingProviderService);
  private layoutService = inject(LayoutService);
  private filterService = inject(FilterService);
  private passportFilterService = inject(PassportFilterService);
  private microAppService = inject(MicroAppService);
  private routerService = inject(RouterService);
  protected router = inject(Router);
  private chameleonService = inject(ChameleonService, { optional: true });
  private healthCheckService = inject(HealthCheckService);
  private zendeskRequestService = inject(ZendeskRequestsService);
  private authGroupService = inject(AuthGroupService);
  private pushMessagesService = inject(PushMessagesService);

  public readonly idPageName = 'layout-container';
  public readonly serverName = getServerName();

  readonly alertState = this.layoutService.alertState;
  readonly showFiltersPanel = this.layoutService.showFiltersPanel;
  readonly showProductPanel = this.layoutService.showProductPanel;
  readonly showHeaderFilterInformation = this.layoutService.showHeaderFilterInformation;
  readonly microAppBasePath = this.microAppService.basePath;
  readonly microAppDisplayName = this.microAppService.displayName;
  private readonly visibleNavItems = this.store.selectSignal(selectVisibleNavItems);

  readonly navItems = computed(() =>
    this.routerService.currentRouteSandBoxed() ? onboardingNavItems : this.visibleNavItems(),
  );
  readonly filterCount$ = this.microAppService
    .getMicroAppCode()
    .pipe(
      mergeMap((app) =>
        app === PRODUCT_CODES.PASSPORT_PLUS
          ? this.passportFilterService.getFilterCount()
          : this.filterService.getFilterCount(),
      ),
    );
  readonly activeNavItemCode = this.microAppService.activeNavItemCode;
  readonly filtersDisabled = this.microAppService.filtersDisabled;
  readonly showMessagingPanel = this.layoutService.showMessagingPanel;
  readonly isAgencyPlatform = this.microAppService.isAgencyApp;
  readonly hospitalOfficerPath = computed(() => this.microAppService.appPath('hospital-officer'));
  // Reads the zendeskRequests slice only once the micro-app is known, and never on the agency platform.
  readonly hasUnreadMessages$ = this.microAppService
    .isAgencyPlatform()
    .pipe(
      switchMap((isAgencyPlatform) =>
        isAgencyPlatform ? of(false) : this.zendeskRequestService.hasUnreadMessages(),
      ),
    );
  readonly microAppList = computed(() =>
    filterMicroAppListForOfficerAuthGroups(
      this.microAppService.microAppList(),
      this.authGroupService.groupNames(),
    ).map((microApp) => ({
      ...microApp,
      // A whole URL, not route commands: switching product closes any open side panel.
      link: this.router.parseUrl(`/${microApp.basePath}`),
    })),
  );

  readonly officerPersonalDetails$ = this.microAppService
    .isAgencyPlatform()
    .pipe(
      switchMap((isAgencyPlatform) =>
        isAgencyPlatform
          ? this.externalStaffingProviderOfficerService.getAssignedUserDetails()
          : this.hospitalOfficerService.getHospitalOfficerPersonalDetails(),
      ),
    );
  readonly organizationDetailsName$ = merge(
    this.externalStaffingProviderService.getAssignedName(),
    this.hospitalService.getAssignedName(),
  );
  readonly headerFilterInfo$ = this.layoutService.getHeaderFilterInformation();
  readonly nextShutDown$ = this.healthCheckService.getNextShutDown();

  readonly menuExpanded = signal(false);
  readonly responsiveMenuOpen = signal(false);

  private scroller = viewChild<ElementRef<HTMLDivElement>>('scroller');

  ngOnInit() {
    this.store.dispatch(new InitializeLayoutContainerSignal({}));
    this.store.dispatch(new CheckForNotificationsPermissionSignal({}));
    this.addToSubscriptions([
      this.routerService
        .getCurrentPath()
        .pipe(mergeMap((path) => this.microAppService.setMicroAppConfiguration(path)))
        .subscribe(),
      this.layoutService.initializeChameleon().subscribe(),
      this.pushMessagesService
        .getMessages()
        .subscribe((msg) => this.refetchRequestAndCommentsSignals(msg)),
      merge(
        this.router.events.pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          // Named-outlet navigations emit NavigationEnd too — opening the help panel, and every
          // Zendesk article search and open — but they leave the page underneath untouched and
          // only change the auxiliary segments in parentheses. Resetting on those threw the user
          // back to the top of whatever list they were reading mid-panel. Compare the primary
          // path alone, so the reset fires on real page changes only.
          map((event) => event.urlAfterRedirects.replace(/\([^)]*\)/g, '')),
          distinctUntilChanged(),
        ),
        this.layoutService.getScrollToTop(),
      ).subscribe(() => this.scroller()?.nativeElement.scrollTo({ top: 0 })),
    ]);
  }

  refetchRequestAndCommentsSignals(msg: IPushMessage) {
    this.store.dispatch(new FetchRequestByIdSignal({ id: +msg.ticketId, showNotification: true }));
    this.store.dispatch(new GetCommentsForRequestSignal({ requestId: +msg.ticketId }));
  }

  onMessagingMenuClicked() {
    this.store.dispatch(new ShowMessagingPanelSignal({}));
  }

  onToggleFiltersPanel() {
    this.store.dispatch(new ToggleFiltersPanelSignal({}));
  }

  toggleProductPanel() {
    this.store.dispatch(new ToggleProductPanelSignal({}));
  }

  onOptionClicked() {
    this.responsiveMenuOpen.set(false);
    this.store.dispatch(new ShowProductPanelSignal({ show: false }));
  }

  onMenuExpanded(expanded: boolean) {
    this.menuExpanded.set(expanded);
  }

  onResponsiveMenuOpen() {
    this.responsiveMenuOpen.update((open) => !open);
  }

  public onAlertToastClicked() {
    this.layoutService.resetAlert();
  }

  onPersonalDetailsClicked() {
    this.store.dispatch(new ShowHideHeaderFilterInformationSignal({}));
  }

  changeProduct(event: MouseEvent, newProduct: IMicroAppListItemState) {
    // Modified clicks open the product in a new tab; this tab stays on its current product.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    this.responsiveMenuOpen.set(false);
    this.chameleonService?.trackProductChange(newProduct.display);
    this.store.dispatch(new ShowProductPanelSignal({ show: false }));
    this.store.dispatch(new ShowFiltersPanelSignal({ show: false }));
    this.store.dispatch(new InitializeLayoutContainerSignal({}));
  }

  logout() {
    this.router.navigateByUrl('/logout');
  }
}
